import { ReactNode, useEffect, useReducer } from "react"
import { useAuth } from "./hooks/useAuth"
import { uploadService } from "./services/uploadService"
import {
	UploadContext,
	uploadReducer,
	emptyState,
	getInitialStateFromLocalStorage,
	LOCAL_STORAGE_KEY,
} from "./contexts/UploadContext"

const MESSAGE_ON_LEAVE =
	"You have active uploads. If you leave, the uploads will be paused."

/**
 * Props for the UploadProvider component.
 */
export interface UploadProviderProps {
	children: ReactNode
}

/**
 * Provides the upload context to its children components.
 * Handles persistence of upload tasks to localStorage and rehydration on mount.
 * @param props - The component props.
 * @param props.children - The child components to render.
 */
export const UploadProvider: React.FC<UploadProviderProps> = ({ children }) => {
	const { user } = useAuth()
	const [uploadState, dispatchUploadAction] = useReducer(
		uploadReducer,
		emptyState,
		getInitialStateFromLocalStorage
	)

	// Save tasks to localStorage whenever they change
	useEffect(() => {
		try {
			// Create a version of tasks without the File object for storage
			const tasksToStore = uploadState.tasks.map(({ file, ...rest }) => rest)
			localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tasksToStore))
		} catch (error) {
			console.error("Failed to save tasks to localStorage:", error)
		}
	}, [uploadState.tasks])

	// Effect to process pending tasks with concurrent upload limit
	useEffect(() => {
		if (!user) return // Don't process if user is not available

		// Count currently uploading tasks
		const uploadingCount = uploadState.tasks.filter(
			(task) => task.status === "uploading"
		).length

		// Maximum concurrent uploads
		const MAX_CONCURRENT_UPLOADS = 30

		// If we're at the limit, don't process more
		if (uploadingCount >= MAX_CONCURRENT_UPLOADS) return

		// Calculate how many more uploads we can start
		const slotsAvailable = MAX_CONCURRENT_UPLOADS - uploadingCount

		// Find pending tasks and process up to the available slots
		const pendingTasks = uploadState.tasks.filter(
			(task) => task.status === "pending" && task.file && task.caseId
		)

		pendingTasks.slice(0, slotsAvailable).forEach((task) => {
			if (user) {
				uploadService.initiateUpload(task, user, dispatchUploadAction)
			}
		})

		// Handle pending_resume tasks
		const pendingResumeTasks = uploadState.tasks.filter(
			(task) =>
				task.status === "pending_resume" && task.file && task.sessionUri
		)

		// Process pending_resume tasks within the remaining slots
		const remainingSlots = slotsAvailable - pendingTasks.slice(0, slotsAvailable).length
		
		pendingResumeTasks.slice(0, remainingSlots).forEach((task) => {
			if (user && task.sessionUri) {
				uploadService.performResumableUpload(task, user, dispatchUploadAction)
			} else if (user && !task.sessionUri) {
				console.warn(
					`Task ${task.id} is pending_resume but has no sessionUri to resume with directly.`
				)
			}
		})
	}, [uploadState.tasks, user, dispatchUploadAction])

	// Effect to warn user before closing tab if there are active uploads
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			const hasActiveUploads = uploadState.tasks.some(
				(task) =>
					task.status === "uploading" ||
					task.status === "pending" ||
					task.status === "pending_resume"
			)

			if (hasActiveUploads) {
				event.preventDefault() // Standard for most browsers
				event.returnValue = MESSAGE_ON_LEAVE // For older browsers
				return MESSAGE_ON_LEAVE // For some browsers
			}
		}

		window.addEventListener("beforeunload", handleBeforeUnload)

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload)
		}
	}, [uploadState.tasks]) // Re-run if tasks change

	return (
		<UploadContext.Provider value={{ uploadState, dispatchUploadAction }}>
			{children}
		</UploadContext.Provider>
	)
}
